import React, { useState } from 'react';
import { loader } from 'graphql.macro';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import { useParams } from '@reach/router';
import { PolicyInfo } from './PolicyInfo';
import { policyIsAgencyBilled } from '../../util/agencyBilledUtils';
import useDocumentDownload from '../../util/useDocumentDownload';
import useFeatureFlag from '../../util/hooks/useFeatureFlag';
import {
  GET_POLICY_DOCUMENT,
  GET_POLICY_DOCUMENTVariables
} from './graphql/__generated__/GET_POLICY_DOCUMENT';
import {
  CancelButton,
  ErrorContainer,
  ErrorDescription,
  ErrorIcon,
  ErrorMessage,
  LoadingMessage,
  LoadingSubtext,
  ModalContainer,
  ModalOverlay,
  Spinner
} from './PolicyInfoStyles';

const get_policy_info_query = loader('./graphql/Get_Policy_Info.graphql');

const get_policy_payplan_billing_method = loader(
  './graphql/Policy_Info_Get_Policy_PayPlan_Billing_Method.graphql'
);

const get_next_payment_no_mortgagee_billed_details = loader(
  './graphql/Policy_Info_Get_Next_Payment_No_Mortgagee_Billed_Details.graphql'
);

const get_policy_document = loader('./graphql/Get_Policy_Document.graphql');

export const transformData = (data: any) => {
  const information = data?.account?.items?.[0]?.policy ?? null;
  if (information?.status === 'InForce') information.status = 'In Force';
  if(information?.status === 'Expired' && information?.migrated === 'Y') information.status = 'Migrated';
  return {
    information
  };
};

export default () => {
  const { policyNumber, accountNumber } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState<Boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(
    'There was an error in loading document. Please try again.'
  );

  const { flagDetails: lastDayToPayFlagDetails } = useFeatureFlag(
    'LastDayToPay'
  );

  const { onDocumentClick } = useDocumentDownload(
    accountNumber,
    'policy',
    'click-policyDeclarationDocument'
  );

  const variables = {
    account_number: accountNumber,
    policy_number: policyNumber
  };

  const accountLevelVariables = {
    account_number: accountNumber
  };

  const {
    data: getPolicyInfoQueryResult,
    loading: policyInfoLoading,
    error: policyInfoError
  } = useQuery(get_policy_info_query, {
    variables
  });

  const { data: billingMethodQueryResult } = useQuery(
    get_policy_payplan_billing_method,
    {
      variables: accountLevelVariables
    }
  );

  const { data: lastDayToPayResult } = useQuery(
    get_next_payment_no_mortgagee_billed_details,
    {
      variables: accountLevelVariables
    }
  );

  let transformedPolicyInfoData = getPolicyInfoQueryResult;

  if (getPolicyInfoQueryResult) {
    transformedPolicyInfoData = transformData(getPolicyInfoQueryResult);
  }

  const lastDayToPayDetails =
    lastDayToPayResult?.account?.items?.[0]?.next_payment_no_mortgagee_billed;

  const [getPolicyDocument, { loading }] = useLazyQuery<
    GET_POLICY_DOCUMENT,
    GET_POLICY_DOCUMENTVariables
  >(get_policy_document, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (
        data &&
        data?.account &&
        data?.account?.items &&
        data?.account?.items.length > 0 &&
        data?.account?.items[0] &&
        data?.account?.items[0]?.policy &&
        data?.account?.items[0]?.policy?.document &&
        data?.account?.items[0]?.policy?.document?.url
      ) {
        const documentData = data?.account?.items[0]?.policy?.document?.url;
        window.open(documentData, '_blank');
      } else {
        console.log('data::', data);
      }
    },
    onError(error) {
      if (error?.message.includes('Could not get document from DynamoDB')) {
        setError(true);
        setShowModal(true);
        setErrorMessage('Policy Document is not available. To view all generated documents, please check the Documents tab.');
      } else {
        setError(true);
        setShowModal(true);
      }
    }
  });

  return (
    <>
      {loading && (
        <ModalOverlay>
          <ModalContainer>
            {loading && (
              <>
                <Spinner />
                <LoadingMessage>Preparing your document</LoadingMessage>
                <LoadingSubtext>
                  Please wait while we fetch your document. This may take a few
                  moments.
                </LoadingSubtext>
              </>
            )}
          </ModalContainer>
        </ModalOverlay>
      )}
      {showModal && error && (
        <ModalOverlay>
          <ModalContainer>
            {error && (
              <ErrorContainer>
                <ErrorIcon>⚠️</ErrorIcon>
                <ErrorMessage>Download Policy Document</ErrorMessage>
                <ErrorDescription>{errorMessage}</ErrorDescription>
                <div>
                  <CancelButton onClick={() => setShowModal(false)}>
                    CLOSE
                  </CancelButton>
                </div>
              </ErrorContainer>
            )}
          </ModalContainer>
        </ModalOverlay>
      )}
      <PolicyInfo
        isLoading={policyInfoLoading}
        error={policyInfoError}
        {...transformedPolicyInfoData}
        accountNumber={accountNumber}
        policyNumber={policyNumber}
        agencyBilled={policyIsAgencyBilled(
          billingMethodQueryResult,
          policyNumber
        )}
        onDocumentClick={onDocumentClick}
        lastDayToPayFlag={lastDayToPayFlagDetails?.enabled}
        lastDayToPayDetails={lastDayToPayDetails}
        getPolicyDocument={getPolicyDocument}
      />
    </>
  );
};
